import { memo } from 'react';
import styles from './KpiStrip.module.css';
import { InfoTip } from './ui/InfoTip';

/**
 * The KPI row every page opens with.
 *
 * Three rules, and they are the reason this is a component rather than a div
 * on each page:
 *
 *  1. A tile with no value shows a dash and, where the API said why, the
 *     reason. It never shows 0. Zero is a business fact; missing is not.
 *  2. A delta is only rendered when the API actually gave one. Several figures
 *     here deliberately have no comparison — "open POs right now" is a state,
 *     not a measure of the window — and inventing one would be a lie the page
 *     tells quietly.
 *  3. `invert` marks the metrics where down is good: average rank, ACOS, days
 *     of cover falling, discount depth. Without it the page congratulates a
 *     team for getting worse in green.
 */
export interface Kpi {
  key: string;
  label: string;
  /** Already formatted. The tile does no number formatting of its own. */
  value: string;
  /** Signed, e.g. "+4.2%". Omit and no delta renders. */
  delta?: string | undefined;
  /** 'up' reads green, 'down' red. Omit for a neutral delta. */
  tone?: 'up' | 'down' | undefined;
  /** Small print under the value — the previous period, or a qualifier. */
  foot?: string | undefined;
  /** Hover definition. */
  help?: string | undefined;
  /** Why the value is missing, when it is. */
  unavailable?: string | undefined;
  /** Pulls the eye to the one or two figures the page is really about. */
  emphasis?: boolean;
}

function KpiStripImpl({ items, loading }: { items: Kpi[]; loading?: boolean }) {
  return (
    <div className={styles.strip} data-testid="kpi-strip">
      {items.map((k) => (
        <div
          key={k.key}
          className={`${styles.tile} ${k.emphasis ? styles.emphasis : ''}`}
          data-kpi={k.key}
        >
          <div className={styles.label}>
            <span>{k.label}</span>
            {k.help ? <InfoTip text={k.help} /> : null}
          </div>
          <div className={styles.valueRow}>
            <span className={`${styles.value} ${loading ? styles.skeleton : ''}`}>
              {loading ? '' : k.value}
            </span>
            {!loading && k.delta ? (
              <span className={`${styles.delta} ${k.tone ? styles[k.tone] : ''}`}>{k.delta}</span>
            ) : null}
          </div>
          {!loading && (k.foot || k.unavailable) ? (
            <div className={`${styles.foot} ${k.unavailable ? styles.footMuted : ''}`}>
              {k.unavailable ?? k.foot}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export const KpiStrip = memo(KpiStripImpl);
